import { useEffect, useState } from 'react';
import { feedbackSummary, type FeedbackSummary } from '../api';
import type { FeedbackLocale, FeedbackTranslate } from '../i18n';
import { Button, Card, Segmented, TableSkeleton } from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import { errorText } from '../../../shell/errors';

type Span = 'week' | 'month';

/** Полночь по Алматы (UTC+5) — граница дня в сводке и в отчётах. */
function localMidnight(daysAgo: number): Date {
  const offsetMs = 5 * 60 * 60_000;
  const nowLocal = new Date(Date.now() + offsetMs);
  const midnightLocal = Date.UTC(nowLocal.getUTCFullYear(), nowLocal.getUTCMonth(), nowLocal.getUTCDate() - daysAgo);
  return new Date(midnightLocal - offsetMs);
}

/**
 * Текстовая сводка за период (М5.7): то, что руководитель читает вместо
 * таблиц. Считается на сервере без языковой модели, одинаково для одинаковых
 * данных; здесь — выбор периода и копирование в мессенджер.
 */
export function SummaryCard({
  t,
  locale,
  siteId,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  siteId: string | null;
}) {
  const toast = useToast();
  const [span, setSpan] = useState<Span>('week');
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);

  useEffect(() => {
    let alive = true;
    setSummary(null);
    const to = localMidnight(-1);
    const from = localMidnight(span === 'week' ? 6 : 29);
    feedbackSummary({ from: from.toISOString(), to: to.toISOString(), locale, ...(siteId ? { siteId } : {}) })
      .then((next) => alive && setSummary(next))
      .catch((err: unknown) => {
        const shown = errorText(err);
        toast.error(shown.message, shown.code);
      });
    return () => {
      alive = false;
    };
  }, [span, locale, siteId, toast]);

  return (
    <Card
      title={t('feedback.sum.title')}
      hint={t('feedback.sum.hint')}
      actions={
        <>
          <Segmented
            active={span}
            onSelect={setSpan}
            label={t('feedback.sum.title')}
            items={[
              { key: 'week', label: t('feedback.sum.week') },
              { key: 'month', label: t('feedback.sum.month') },
            ]}
          />
          <Button
            disabled={!summary}
            onClick={() => {
              if (!summary) return;
              void navigator.clipboard?.writeText(summary.text).then(() => toast.ok(t('feedback.sum.copied')));
            }}
          >
            {t('feedback.sum.copy')}
          </Button>
        </>
      }
    >
      {summary ? (
        <div className="space-y-2 text-sm leading-relaxed text-ink">
          {summary.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      ) : (
        <TableSkeleton rows={4} columns={1} label={t('feedback.common.loading')} />
      )}
    </Card>
  );
}
